import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { NIGERIAN_STATES } from "@/lib/constants";
import { lgasForState } from "@/lib/lga";
import { MapPin, Store, Search, Loader2, Navigation, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import { VendorLgaMap } from "@/components/VendorLgaMap";
import { normLga, titleLga, vendorPos } from "@/lib/lgaCoords";

export const STATE_COORDS = {
  "Abia": [5.4527, 7.5248], "Adamawa": [9.3265, 12.3984], "Akwa Ibom": [5.0074, 7.8536],
  "Anambra": [6.2209, 6.9370], "Bauchi": [10.3103, 9.8439], "Bayelsa": [4.7719, 6.0699],
  "Benue": [7.3369, 8.7404], "Borno": [11.8333, 13.1500], "Cross River": [5.8702, 8.5988],
  "Delta": [5.7040, 5.9339], "Ebonyi": [6.2649, 8.0137], "Edo": [6.3350, 5.6037],
  "Ekiti": [7.7189, 5.3110], "Enugu": [6.5244, 7.5186], "Gombe": [10.2897, 11.1673],
  "Imo": [5.4830, 7.0257], "Jigawa": [12.2280, 9.5616], "Kaduna": [10.5222, 7.4383],
  "Kano": [12.0022, 8.5919], "Katsina": [12.9908, 7.6018], "Kebbi": [11.4942, 4.2333],
  "Kogi": [7.7337, 6.6906], "Kwara": [8.9669, 4.3874], "Lagos": [6.5244, 3.3792],
  "Nasarawa": [8.5378, 8.3206], "Niger": [9.9309, 5.5983], "Ogun": [7.1608, 3.3483],
  "Ondo": [7.2508, 5.2103], "Osun": [7.5629, 4.5200], "Oyo": [8.1574, 3.6147],
  "Plateau": [9.2182, 9.5179], "Rivers": [4.8156, 7.0498], "Sokoto": [13.0059, 5.2476],
  "Taraba": [7.9994, 10.7740], "Yobe": [12.2939, 11.4390], "Zamfara": [12.1704, 6.6641],
  "FCT Abuja": [9.0765, 7.3986],
};

const haversine = (a, b) => {
  const R = 6371, r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export default function FindVendor() {
  const [state, setState] = useState("");
  const [lga, setLga] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState(null);
  const [me, setMe] = useState(null);
  const [nearestOpen, setNearestOpen] = useState(false);
  const [myState, setMyState] = useState("");

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      if (state) params.state = state;
      const { data } = await api.get("/vendors", { params });
      let list = data.vendors.map((v) => {
        const p = vendorPos(v, STATE_COORDS);
        return p ? { ...v, lat: p[0], lng: p[1] } : v;
      });
      if (lga) list = list.filter((v) => normLga(v.lga) === normLga(lga));
      if (me) list = [...list].sort((a, b) => haversine(me, a) - haversine(me, b));
      setVendors(list);
      setSearched(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { search(); }, []);

  const confirmNearest = () => {
    if (!myState) return toast.error("Please select your current state.");
    const [lat, lng] = STATE_COORDS[myState];
    const loc = { lat, lng, state: myState };
    setMe(loc);
    setVendors((v) => [...v].sort((a, b) => (a.lat == null) - (b.lat == null) || haversine(loc, a) - haversine(loc, b)));
    setNearestOpen(false);
    toast.success(`Vendors sorted by distance from ${myState}.`);
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-12">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Vendor Network</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950">Find a TMF Vendor</h1>
        <p className="mt-2 text-sm text-gray-600">Locate an authorised vendor anywhere in Nigeria. Your benefit can be redeemed at any of them.</p>

        <div className="mt-6 rounded-2xl border border-emerald-100 bg-white p-5 grid sm:grid-cols-4 gap-4 items-end">
          <div>
            <Label>State</Label>
            <Select value={state} onValueChange={(v) => { setState(v); setLga(""); }}>
              <SelectTrigger data-testid="vendor-search-state" className="mt-1.5 h-11"><SelectValue placeholder="Any state" /></SelectTrigger>
              <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>LGA</Label>
            <Select value={lga} onValueChange={setLga} disabled={!state}>
              <SelectTrigger data-testid="vendor-search-lga" className="mt-1.5 h-11"><SelectValue placeholder={state ? "Any LGA" : "Select state first"} /></SelectTrigger>
              <SelectContent>{lgasForState(state).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="vendor-search-btn" onClick={search} className="h-11 bg-emerald-600 hover:bg-emerald-700 rounded-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Search</>}
          </Button>
          <Button data-testid="vendor-nearest-btn" onClick={() => setNearestOpen(true)} variant="outline" className="h-11 rounded-full border-emerald-200 text-emerald-800">
            <Navigation className="w-4 h-4 mr-2" /> Nearest to me
          </Button>
        </div>

        <Dialog open={nearestOpen} onOpenChange={setNearestOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Find vendors nearest to you</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-600">Select your current state and we'll sort authorised TMF vendors by distance. No location tracking required.</p>
            <div className="mt-2">
              <Label>Your current state</Label>
              <Select value={myState} onValueChange={setMyState}>
                <SelectTrigger data-testid="nearest-state-select" className="mt-1.5 h-11"><SelectValue placeholder="Select your state" /></SelectTrigger>
                <SelectContent>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button data-testid="nearest-confirm-btn" onClick={confirmNearest} className="bg-emerald-600 hover:bg-emerald-700"><Navigation className="w-4 h-4 mr-2" /> Sort by distance</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          {/* Interactive schematic map */}
          <div className="lg:col-span-3" data-testid="vendor-map">
            <VendorLgaMap vendors={vendors} stateCoords={STATE_COORDS} height={520} />
          </div>

          {/* Vendor list */}
          <div className="lg:col-span-2 space-y-4 max-h-[520px] overflow-auto pr-1">
            {vendors.map((v) => (
              <div
                key={v.vendor_id}
                data-testid={`vendor-card-${v.vendor_id}`}
                onClick={() => setSelected(v)}
                className={`rounded-2xl border bg-white p-5 cursor-pointer transition-all ${selected?.vendor_id === v.vendor_id ? "border-emerald-400 shadow-md" : "border-emerald-100 hover:shadow-sm"}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><Store className="w-5 h-5 text-emerald-600" /></div>
                  {me && v.lat != null && <span className="text-xs font-semibold text-emerald-700">{haversine(me, v).toFixed(0)} km</span>}
                </div>
                <h3 className="mt-3 font-display font-semibold text-emerald-950">{v.business_name}</h3>
                <div className="mt-1.5 space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-500" /> {v.address || "—"}, {titleLga(normLga(v.lga))}, {v.state}</div>
                  {v.landmark && <div className="text-xs text-gray-500 pl-5">Near: {v.landmark}</div>}
                  {v.hours && <div className="flex items-center gap-2 text-xs"><Clock className="w-3.5 h-3.5 text-emerald-500" /> {v.hours}</div>}
                  {v.phone && <a href={`tel:${v.phone}`} onClick={(e) => e.stopPropagation()} data-testid={`vendor-call-${v.vendor_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline"><Phone className="w-3.5 h-3.5" /> Call shop</a>}
                  <div className="font-mono text-xs text-gray-400">{v.vendor_id}</div>
                </div>
              </div>
            ))}
            {searched && vendors.length === 0 && (
              <div className="text-center text-gray-500 py-16 border border-dashed border-emerald-200 rounded-2xl">
                No vendors found. Vendors appear here once they register via <b>Become a TMF Vendor</b>.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
